import React, { useState, useEffect } from "react";
import AuthLayout from "../../../../layout/AuthLayout";
import {
  Box,
  Flex,
  Text,
  Group,
  TextInput,
  Button,
  Pagination,
  Modal,
  ScrollArea,
  Table,
  ActionIcon,
  Image,
  Badge,
} from "@mantine/core";
import classes from "../../../../assets/module-css/AdminLayout.module.css";
import { useDispatch } from "react-redux";
import { updateRoute } from "../../../../store/dataRoute";
import {
  getCategorysList,
  deleteCategory,
  getCategoryTotalProducts,
} from "../../../../services/category";
import { useDebouncedCallback } from "@mantine/hooks";
import LoadingData from "../../../../component/ui/LoadingData";
import NoData from "../../../../component/ui/NoData";
import { notifications } from "@mantine/notifications";
import {
  IconCheck,
  IconX,
  IconPlus,
  IconPencil,
  IconTrash,
  IconPhotoEdit,
  IconArrowRight,
  IconArrowLeft,
} from "@tabler/icons-react";
import { modals } from "@mantine/modals";
import FormProductCategory from "./FormProductCategory";
import FormEditImage from "./FormEditImage";

const TotalProductCell = ({ categoryId }) => {
  const [total, setTotal] = useState("-");

  useEffect(() => {
    let active = true;
    const fetchTotal = async () => {
      try {
        const res = await getCategoryTotalProducts(categoryId);
        if (active) {
          setTotal(res.total_products || 0);
        }
      } catch (e) {
        if (active) setTotal(0);
      }
    };
    fetchTotal();
    return () => {
      active = false;
    };
  }, [categoryId]);

  return <Text size="sm">{total}</Text>;
};

const defaultParameter = {
  "page[num]": 1,
  "page[limit]": 5,
  "filter[name][like]": "",
  "filter[category_type][is]": 1,
  "filter[level][is]": 1,
  include: "child,rules",
};

const ProductCategory = () => {
  const dispatch = useDispatch();
  const [params, setParams] = useState(defaultParameter);
  const [dataList, setDataList] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [loading, setLoading] = useState(true);
  const [includedData, setIncludedData] = useState(null);

  const [openModalForm, setOpenModalForm] = useState(false);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [parentHistory, setParentHistory] = useState([]);

  useEffect(() => {
    dispatch(
      updateRoute({
        data: [
          { label: "/", route: null },
          { label: "Products", route: null },
          { label: "Product Category", route: null },
        ],
      }),
    );
  }, [dispatch]);

  const fetchCategories = useDebouncedCallback(async () => {
    setLoading(true);
    try {
      const response = await getCategorysList(params);
      if (response?.data?.data) {
        setDataList(response.data.data);
        setTotalData(response.data.meta?.pagination?.total || 0);
        setIncludedData(response.data.included || {});
      } else if (response?.data) {
        setDataList(response.data || []);
        setTotalData(response.meta?.pagination?.total || 0);
        setIncludedData(response.included || {});
      } else {
        setDataList([]);
        setTotalData(0);
        setIncludedData(null);
      }
    } catch (error) {
      console.log(error);
      setDataList([]);
      setTotalData(0);
      setIncludedData(null);
    } finally {
      setLoading(false);
    }
  }, 500);

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line
  }, [params]);

  const handleChangeFilter = (name, val) => {
    setParams((oldVal) => ({ ...oldVal, [name]: val, "page[num]": 1 }));
  };

  const handleChangePage = (val) => {
    setParams((oldVal) => ({ ...oldVal, "page[num]": val }));
  };

  const handleDelete = (val) => {
    modals.openConfirmModal({
      title: "Delete Category",
      children: (
        <Text size="sm">
          Are you sure you want to delete this Category? This action cannot be
          undone.
        </Text>
      ),
      labels: { confirm: "Delete", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          const response = await deleteCategory(val.id);
          if (response.success || response.message) {
            notifications.show({
              title: "Success",
              message: "Category deleted successfully",
              color: "teal",
              icon: <IconCheck />,
            });
            fetchCategories();
          }
        } catch (error) {
          notifications.show({
            title: "Error",
            message: error?.response?.data?.message || "Something went wrong",
            color: "red",
            icon: <IconX />,
          });
        }
      },
    });
  };

  const handleAdd = () => {
    setDetailData(null);
    setOpenModalForm(true);
  };

  const handleEdit = (val) => {
    setDetailData(val);
    setOpenModalForm(true);
  };

  const handleEditImage = (val) => {
    setDetailData(val);
    setOpenImageModal(true);
  };

  const handleGoChild = (item) => {
    setParentHistory((prev) => [
      ...prev,
      { id: item.id, name: item.attributes.name },
    ]);
    setParams((old) => {
      const newParams = { ...old, "page[num]": 1 };
      newParams["filter[parent_id][is]"] = item.id;
      newParams["filter[level][is]"] = (item.attributes.level || 1) + 1;
      return newParams;
    });
  };

  const handleGoBack = () => {
    setParentHistory((prev) => {
      const newHistory = [...prev];
      newHistory.pop();
      return newHistory;
    });
    setParams((old) => {
      const newParams = { ...old, "page[num]": 1 };
      const previousParent = parentHistory[parentHistory.length - 2];

      if (previousParent) {
        newParams["filter[parent_id][is]"] = previousParent.id;
        newParams["filter[level][is]"] = newParams["filter[level][is]"] - 1;
      } else {
        delete newParams["filter[parent_id][is]"];
        newParams["filter[level][is]"] = 1;
      }
      return newParams;
    });
  };

  const theaders = (
    <Table.Tr>
      <Table.Th>ID</Table.Th>
      <Table.Th>Image</Table.Th>
      <Table.Th>Name</Table.Th>
      <Table.Th>Level</Table.Th>
      <Table.Th>Priority</Table.Th>
      <Table.Th>Total Product</Table.Th>
      <Table.Th>Category Rule</Table.Th>
      <Table.Th ta="center">Action</Table.Th>
    </Table.Tr>
  );

  const trows = dataList.map((item) => (
    <Table.Tr key={item.id}>
      <Table.Td>{item.id}</Table.Td>
      <Table.Td>
        {item.attributes.image ? (
          <Image
            src={item.attributes.image}
            w={50}
            h={50}
            radius="sm"
            fit="contain"
          />
        ) : (
          <Text size="xs" c="dimmed">
            No Image
          </Text>
        )}
      </Table.Td>
      <Table.Td>{item.attributes.name}</Table.Td>
      <Table.Td>{item.attributes.level || 1}</Table.Td>
      <Table.Td>{item.attributes.priority}</Table.Td>
      <Table.Td>
        <TotalProductCell categoryId={item.id} />
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          {(item.relationships?.rules)
            ?.length > 0 ? (
            (item.relationships.rules).map(
              (rule) => {
                const ruleId = rule.id || rule;
                const ruleDesc =
                  includedData?.rules?.[ruleId]?.attributes?.description ||
                  includedData?.category_rules?.[ruleId]?.attributes
                    ?.description ||
                  `Rule ID: ${ruleId}`;
                return (
                  <Badge key={ruleId} variant="light" size="sm">
                    {ruleDesc}
                  </Badge>
                );
              },
            )
          ) : (
            <Text size="xs" c="dimmed">
              No Rules
            </Text>
          )}
        </Group>
      </Table.Td>
      <Table.Td ta="center">
        <Group gap="xs" justify="center">
          <ActionIcon
            color="blue"
            variant="light"
            onClick={() => handleEdit(item)}
            title="Edit Category"
          >
            <IconPencil size={16} />
          </ActionIcon>
          <ActionIcon
            color="indigo"
            variant="light"
            onClick={() => handleEditImage(item)}
            title="Edit Image"
          >
            <IconPhotoEdit size={16} />
          </ActionIcon>
          <ActionIcon
            color="teal"
            variant="light"
            onClick={() => handleGoChild(item)}
            title="Go to Child Categories"
          >
            <IconArrowRight size={16} />
          </ActionIcon>
          <ActionIcon
            color="red"
            variant="light"
            onClick={() => handleDelete(item)}
            title="Delete Category"
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <AuthLayout>
      <Box my={20}>
        <Box>
          <Flex justify="space-between" align="center" mb={10}>
            <Text className={classes.titleLayout}>Product Category</Text>
            <Group gap="sm" align="center">
              <TextInput
                value={params["filter[name][like]"] || ""}
                placeholder="Search by Name"
                onChange={(val) =>
                  handleChangeFilter("filter[name][like]", val.target.value)
                }
                size="xs"
              />
              <Button
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={handleAdd}
              >
                Add Category
              </Button>
            </Group>
          </Flex>

          {parentHistory.length > 0 && (
            <Group mb={20} gap="xs">
              <Button
                size="xs"
                variant="light"
                leftSection={<IconArrowLeft size={14} />}
                onClick={handleGoBack}
              >
                Back
              </Button>
              <Text size="sm" fw={500}>
                Browsing inside: {parentHistory.map((p) => p.name).join(" > ")}
              </Text>
            </Group>
          )}
        </Box>

        <Box my={30}>
          <Group mb={10} gap="xs">
            <Text fz={12}>Total Data Found: </Text>
            <Text fz={12} fw="bold">
              {totalData} Categories
            </Text>
          </Group>

          {loading ? (
            <LoadingData />
          ) : dataList.length > 0 ? (
            <Table.ScrollContainer minWidth={800}>
              <Table verticalSpacing="sm">
                <Table.Thead>{theaders}</Table.Thead>
                <Table.Tbody>{trows}</Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          ) : (
            <NoData />
          )}
        </Box>

        {totalData > 0 && (
          <Flex justify="end">
            <Pagination
              size="sm"
              onChange={handleChangePage}
              value={params["page[num]"]}
              total={Math.ceil(totalData / params["page[limit]"])}
            />
          </Flex>
        )}
      </Box>

      {/* Form Add/Edit */}
      <Modal
        opened={openModalForm}
        onClose={() => setOpenModalForm(false)}
        centered
        closeOnClickOutside={false}
        size="lg"
        title={
          <Text fw="bold">{detailData ? "Edit Category" : "Add Category"}</Text>
        }
        scrollAreaComponent={ScrollArea.Autosize}
        withCloseButton={false}
      >
        {openModalForm && (
          <FormProductCategory
            dataDetail={detailData}
            onCloseModal={() => setOpenModalForm(false)}
            reloadList={fetchCategories}
            currentParentId={params["filter[parent_id][is]"]}
            currentLevel={params["filter[level][is]"]}
          />
        )}
      </Modal>

      {/* Form Edit Image */}
      <Modal
        opened={openImageModal}
        onClose={() => setOpenImageModal(false)}
        centered
        closeOnClickOutside={false}
        title={<Text fw="bold">Edit Category Image</Text>}
        withCloseButton={false}
      >
        {openImageModal && (
          <FormEditImage
            dataDetail={detailData}
            onCloseModal={() => setOpenImageModal(false)}
            reloadList={fetchCategories}
          />
        )}
      </Modal>
    </AuthLayout>
  );
};

export default ProductCategory;
